import React, { useMemo, useState, useEffect } from "react";
import { Modal, Box, CircularProgress, Alert } from "@mui/material";
import { isAxiosError } from "axios";
import { CancelIcon } from "../../assets/icon";
import { getManagerStatistics } from "../../axios/user";
import { ManagerStatisticsData } from "../../types/statistics";
import useAppStore from "../../stores/appStore";
import {
  modalStyles,
  headerBoxStyles,
  headerRightBoxStyles,
  contentStyles,
} from "./styles";
import { DomainFocusBlock } from "../statistic-modal/domain-focus-block/domain-focus-block";
import { HtmlContent } from "./html-content/html-content";
import { ChartsSection } from "./charts-section/charts-section";
import { FilesSection } from "./files-section/files-section";
import { UserMenuTrigger } from "../user-menu-trigger/user-menu-trigger";
import { CustomTypography } from "../custom-typography/custom-typography";

// Convert array of objects to HTML table
const convertArrayToHtmlTable = (
  dataArray: Array<Record<string, any>>
): string => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return "";
  }

  const headers = Object.keys(dataArray[0]);

  const html = `
    <table>
      <thead>
        <tr>
          ${headers.map((header) => `<th>${header}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${dataArray
          .map(
            (row) => `
          <tr>
            ${headers.map((header) => `<td>${(row[header] ?? "").toString()}</td>`).join("")}
          </tr>
        `
          )
          .join("")}
      </tbody>
    </table>
  `;

  return html;
};

interface ResolvedStatisticsTable {
  title?: string;
  html: string;
}

const resolveManagerStatisticsTables = (
  response: ManagerStatisticsData
): ResolvedStatisticsTable[] => {
  if (!Array.isArray(response.data)) return [];

  return response.data.map((section) => ({
    title: section.title,
    html: convertArrayToHtmlTable(section.lines),
  }));
};

interface ManagerStatisticsModalProps {
  open: boolean;
  onClose: () => void;
}

export const ManagerStatisticsModal: React.FC<ManagerStatisticsModalProps> = ({
  open,
  onClose,
}) => {
  const [data, setData] = useState<ManagerStatisticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { focus, domain } = useAppStore();

  const tables = useMemo(
    () => (data ? resolveManagerStatisticsTables(data) : []),
    [data]
  );

  const tableHtmls = useMemo(
    () => tables.map((table) => table.html).filter(Boolean),
    [tables]
  );

  const hasOtherContent = useMemo(() => {
    if (!data) return false;
    return (
      (data.files?.length ?? 0) > 0 ||
      (data.images?.length ?? 0) > 0 ||
      !!data.charts?.monthlyOverallProgress ||
      !!data.charts?.c4Progress
    );
  }, [data]);

  useEffect(() => {
    if (open) {
      fetchManagerStatistics();
    }
  }, [open, focus, domain]);

  const fetchManagerStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const statisticsData = await getManagerStatistics();
      setData(statisticsData);
    } catch (err) {
      let message = "Failed to load team statistics";

      if (isAxiosError(err)) {
        const status = err.response?.status;

        if (!status) {
          message =
            "Cannot reach the server (network/CORS). Please check your connection and try again.";
        } else if (status === 401) {
          message = "Session expired. Please log in again.";
        } else if (status === 403) {
          message = "Team statistics are available to managers only.";
        } else if (status === 404) {
          message = "No team statistics found for your team.";
        } else {
          message = `Failed to load team statistics (HTTP ${status}).`;
        }
      }

      setError(message);
      console.error("Error fetching manager statistics:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setData(null);
    setError(null);
    onClose();
  };

  const downloadFile = (file: { file: string; name: string; type: string }) => {
    try {
      const byteCharacters = atob(file.file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: `application/${file.type}` });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyles}>
        <Box sx={headerBoxStyles}>
          <DomainFocusBlock />
          <Box sx={headerRightBoxStyles}>
            <UserMenuTrigger isStatistic={true} />
            <CancelIcon
              color="#FFFFFF"
              onClick={onClose}
              style={{ cursor: "pointer" }}
            />
          </Box>
        </Box>

        <Box sx={contentStyles}>
          {loading && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              gap={2}
            >
              <CircularProgress size={48} />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
              >
                <CustomTypography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Downloading team statistics...
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color="text.disabled"
                  sx={{ mt: 0.5 }}
                >
                  Please wait...
                </CustomTypography>
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {data && !loading && (
            <>
              {tables.length > 0 ? (
                tables.map((table, index) => (
                  <HtmlContent
                    key={`${table.title ?? "table"}-${index}`}
                    html={table.html}
                    title={table.title}
                  />
                ))
              ) : (
                !hasOtherContent && <HtmlContent html="" />
              )}
              <ChartsSection
                images={data.images || []}
                charts={data.charts}
                tableHtmls={tableHtmls}
              />
              <FilesSection
                files={data.files || []}
                onDownloadFile={downloadFile}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
